import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, ChatText, EnvelopeSimple, ArrowRight, ArrowLeft, Lock, X, SealCheck, Headset } from "@phosphor-icons/react";
import { QuestProgress } from "../components/ui/quest-progress";
import { getRef, captureRef, clearRef } from "../lib/referral";
import { Button } from "../components/ui/button";
import { BrandMark } from "../components/BrandMark";
import LinkedInInsight from "../components/LinkedInInsight";
import { track } from "../lib/analytics";
import "../booking/booking.css";
import { API, http, STEPS, PHONE, EMAIL, svcApplies } from "./booking/booking-shared";
import { StepAddress } from "./booking/StepAddress";
import { StepPropertyType } from "./booking/StepPropertyType";
import { StepPackage } from "./booking/StepPackage";
import { StepSchedule } from "./booking/StepSchedule";
import { StepDetails } from "./booking/StepDetails";
import { OrderSummary } from "./booking/OrderSummary";
import { Confirmation } from "./booking/Confirmation";

export default function BookingWidget({ embedded = false, currentUser = null, onClose = null }) {
  const [cfg, setCfg] = useState(null);
  const [phase, setPhase] = useState("wizard"); // wizard | confirmed
  const [step, setStep] = useState(0);
  const [query, setQuery] = useState("");
  const [sugg, setSugg] = useState([]);
  const [parcel, setParcel] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const purchaseTracked = useRef(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [conflictTick, setConflictTick] = useState(0);
  const cartToken = useRef(null);
  const saveCartTimer = useRef();

  const [o, setO] = useState(() => ({
    entry: true, package: "all_inclusive", property_type: "residential",
    sqft_lookup: null, sqft_edited: null, acres_lookup: null, acres_edited: null,
    penthouse: true, waive_extras: true, oversized: null, items: ["photos", "video", "staging"], addons: [],
    address: "", state: null, city: null, lat: null, lon: null,
    shoot_date: "", time_window: "", access_method: "Agent will meet",
    occupancy: "Vacant", pets: "None", gate_code: "",
    contact_name: currentUser?.name || "", contact_email: currentUser?.email || "", contact_phone: currentUser?.phone || "",
  }));
  const set = (patch) => setO((p) => ({ ...p, ...patch }));

  useEffect(() => { http.get(`${API}/config`).then((r) => setCfg(r.data)).catch(() => {}); }, []);

  // Capture a referral slug passed as ?ref= so /book bookings stay attributed.
  // Skip entirely when booking from inside the portal — the shoot is the user's own account.
  useEffect(() => {
    if (embedded) { clearRef(); return; }
    const rp = new URLSearchParams(window.location.search).get("ref");
    if (rp) captureRef(rp);
  }, [embedded]);

  // Stripe return
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const b = p.get("booking"), sid = p.get("session_id");
    if (b === "success" && sid) {
      setPhase("confirmed");
      clearRef();
      const poll = async () => {
        try {
          const { data } = await http.get(`${API}/status/${sid}`);
          setReceipt(data.receipt);
          setO((x) => ({ ...x, address: data.address, shoot_date: data.shoot_date, package: data.package, penthouse: data.penthouse }));
          if (data.payment_status !== "paid") setTimeout(poll, 2000);
          else if (!purchaseTracked.current) {
            purchaseTracked.current = true;
            track("purchase", { transaction_id: sid, currency: "USD", value: data.receipt?.total_today || undefined });
          }
        } catch { setTimeout(poll, 2500); }
      };
      poll();
    }
  }, []);

  const acTimer = useRef();
  const onQuery = (v) => {
    setQuery(v); clearTimeout(acTimer.current);
    if (v.trim().length < 3) { setSugg([]); return; }
    acTimer.current = setTimeout(async () => {
      try { const { data } = await http.get(`${API}/autocomplete`, { params: { q: v } }); setSugg(data.results || []); }
      catch { setSugg([]); }
    }, 280);
  };

  const quote = useCallback(async (next) => {
    const s = { ...o, ...next };
    const sqft = s.sqft_edited != null ? s.sqft_edited : s.sqft_lookup;
    const acres = s.acres_edited != null ? s.acres_edited : s.acres_lookup;
    try {
      const { data } = await http.post(`${API}/quote`, {
        entry: s.entry, package: s.package, property_type: s.property_type, sqft, acres,
        penthouse: s.penthouse, waive_extras: s.waive_extras, oversized: s.oversized, state: s.state,
        first_shoot: true, email: s.contact_email || null, items: s.items || null, addons: s.addons || null,
      });
      setReceipt(data);
    } catch {}
  }, [o]);

  // Abandoned-cart capture: once we have a usable email + address, quietly save a draft so the
  // recovery cron can nudge the customer if they don't finish. Public bookings only.
  const saveCart = useCallback((s, total) => {
    if (embedded) return;
    const email = (s.contact_email || "").trim();
    if (!email.includes("@") || !s.address) return;
    clearTimeout(saveCartTimer.current);
    saveCartTimer.current = setTimeout(async () => {
      try {
        const { data } = await http.post(`${API}/cart`, {
          cart_token: cartToken.current,
          entry: s.entry, package: s.package, property_type: s.property_type,
          sqft_lookup: s.sqft_lookup, sqft_edited: s.sqft_edited, acres_lookup: s.acres_lookup, acres_edited: s.acres_edited,
          penthouse: s.penthouse, waive_extras: s.waive_extras, oversized: s.oversized, items: s.items || null, addons: s.addons || null,
          address: s.address, state: s.state, city: s.city, lat: s.lat, lon: s.lon,
          shoot_date: s.shoot_date, time_window: s.time_window, access_method: s.access_method,
          occupancy: s.occupancy, pets: s.pets, gate_code: s.gate_code,
          contact_name: s.contact_name, contact_email: email, contact_phone: s.contact_phone,
          quote_total: total ?? null, ref_slug: getRef(),
        });
        if (data?.cart_token) cartToken.current = data.cart_token;
      } catch {}
    }, 900);
  }, [embedded]);

  useEffect(() => {
    saveCart(o, receipt?.total_today);
  }, [o.contact_email, o.address, o.package, o.shoot_date, o.time_window, o.items, o.addons, o.penthouse, receipt?.total_today, saveCart]);

  // One-tap recovery: ?cart=<token> prefills a saved booking and jumps to the final step.
  useEffect(() => {
    if (embedded) return;
    const token = new URLSearchParams(window.location.search).get("cart");
    if (!token) return;
    (async () => {
      try {
        const { data } = await http.get(`${API}/cart/${token}`);
        cartToken.current = token;
        const patch = {
          entry: data.entry, package: data.package, property_type: data.property_type,
          sqft_lookup: data.sqft_lookup, sqft_edited: data.sqft_edited, acres_lookup: data.acres_lookup, acres_edited: data.acres_edited,
          penthouse: data.penthouse, waive_extras: data.waive_extras, oversized: data.oversized, items: data.items || [], addons: data.addons || [],
          address: data.address, state: data.state, city: data.city, lat: data.lat, lon: data.lon,
          shoot_date: data.shoot_date || "", time_window: data.time_window || "",
          access_method: data.access_method || "Agent will meet", occupancy: data.occupancy || "Vacant",
          pets: data.pets || "None", gate_code: data.gate_code || "",
          contact_name: data.contact_name || "", contact_email: data.contact_email || "", contact_phone: data.contact_phone || "",
        };
        setO((x) => ({ ...x, ...patch }));
        setQuery(data.address || "");
        await quote(patch);
        setStep(4);
      } catch {}
    })();
  }, [embedded]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectAddress = async (item) => {
    const label = item.label || item.display || query;
    setSugg([]); setQuery(label); setBusy(true); setErr("");
    let pr = null;
    try { const { data } = await http.get(`${API}/parcel`, { params: { address: label, lat: item.lat, lon: item.lon } }); pr = data; } catch {}
    setParcel(pr);
    const next = {
      address: label, state: pr?.state || item.state || null, city: pr?.city || item.city || null,
      lat: pr?.lat || item.lat || null, lon: pr?.lon || item.lon || null,
      acres_lookup: pr?.acres ?? null, acres_edited: null, sqft_lookup: pr?.sqft ?? null, sqft_edited: null,
    };
    set(next);
    await quote(next);
    setBusy(false);
  };

  const pickType = (t) => {
    const wasLand = o.property_type === "land";
    const patch = { property_type: t.id, entry: t.entry };
    // Land/acreage never needs interior capture — force build-your-own with drone only.
    if (t.id === "land") {
      patch.package = "a_la_carte";
      patch.items = ["drone_photo"];
      patch.penthouse = false;
    } else if (wasLand) {
      // Leaving land — restore the default All-Inclusive experience (undo the forced à-la-carte).
      patch.package = "all_inclusive";
      patch.penthouse = true;
      patch.waive_extras = true;
      patch.items = ["photos", "video", "staging"];
    } else if (o.package === "a_la_carte") {
      patch.items = (o.items || []).filter((k) => svcApplies(cfg, k, t.id));
    }
    set(patch); quote(patch);
  };
  const pickPackage = (pkg) => {
    const patch = { package: pkg };
    if (pkg === "all_inclusive") { patch.penthouse = true; patch.waive_extras = true; }
    else if (pkg === "a_la_carte") {
      patch.penthouse = false; patch.addons = [];
      if (!o.items || !o.items.length) patch.items = ["photos", "video", "staging"];
    }
    set(patch); quote(patch);
  };
  const toggleAddon = (key) => {
    const has = (o.addons || []).includes(key);
    const addons = has ? o.addons.filter((k) => k !== key) : [...(o.addons || []), key];
    set({ addons }); quote({ addons });
  };
  const toggleItem = (key) => {
    if (o.property_type === "land" && key === "drone_photo") return; // mandatory base for land
    const has = (o.items || []).includes(key);
    const items = has ? o.items.filter((k) => k !== key) : [...(o.items || []), key];
    set({ items }); quote({ items });
  };
  const toggleOversized = () => {
    const cur = o.oversized;
    const next = cur === null ? true : cur === true ? false : null;
    set({ oversized: next }); quote({ oversized: next });
  };
  const toggleWaive = () => { const v = !o.waive_extras; set({ waive_extras: v }); quote({ waive_extras: v }); };
  const editSqft = (v) => { const n = v === "" ? null : Math.max(0, parseInt(v, 10) || 0); set({ sqft_edited: n }); quote({ sqft_edited: n }); };
  const editAcres = (v) => { const n = v === "" ? null : Math.max(0, parseFloat(v) || 0); set({ acres_edited: n }); quote({ acres_edited: n }); };

  const inService = !o.state || (cfg && cfg.service_states.includes(o.state));
  const sqft = o.sqft_edited != null ? o.sqft_edited : o.sqft_lookup;
  const acres = o.acres_edited != null ? o.acres_edited : o.acres_lookup;

  const stepValid = [
    !!o.address && !!o.state,            // address
    o.package !== "a_la_carte" || !!(o.items && o.items.length > 0),  // package
    !!o.property_type,                    // property type & size
    !!o.shoot_date && !!o.time_window,    // schedule
    !!o.contact_email,                    // details
  ];

  const checkout = async () => {
    setBusy(true); setErr("");
    if (o.state && cfg && !cfg.service_states.includes(o.state)) {
      try {
        await http.post(`${API}/lead`, { address: o.address, state: o.state, contact_name: o.contact_name, contact_email: o.contact_email, contact_phone: o.contact_phone, note: "Out of service area", ref_slug: embedded ? null : getRef() });
        setErr("We're not in your area yet, but we've saved your details and will reach out.");
      } catch { setErr("Couldn't save your request. Please try again."); }
      setBusy(false); return;
    }
    if (!o.contact_email) { setErr("Add an email so we can send your confirmation."); setBusy(false); return; }
    try {
      const { data } = await http.post(`${API}/checkout`, {
        origin_url: window.location.origin,
        entry: o.entry, package: o.package, property_type: o.property_type,
        sqft_lookup: o.sqft_lookup, sqft_edited: o.sqft_edited, acres_lookup: o.acres_lookup, acres_edited: o.acres_edited,
        penthouse: o.penthouse, waive_extras: o.waive_extras, oversized: o.oversized, items: o.items || null, addons: o.addons || null,
        address: o.address, state: o.state, city: o.city, lat: o.lat, lon: o.lon,
        shoot_date: o.shoot_date, time_window: o.time_window, access_method: o.access_method,
        occupancy: o.occupancy, pets: o.pets, gate_code: o.gate_code,
        contact_name: o.contact_name, contact_email: o.contact_email, contact_phone: o.contact_phone,
        ref_slug: embedded ? null : getRef(),
      });
      track("begin_checkout", { currency: "USD", value: receipt?.total_today || undefined });
      window.top.location.href = data.checkout_url;
    } catch (e) {
      if (e.response?.status === 409) {
        setErr(e.response.data.detail || "That slot just filled up. Please pick another.");
        setConflictTick((t) => t + 1); set({ time_window: "" }); setStep(3);
      } else setErr(e.response?.data?.detail || "Couldn't start checkout.");
      setBusy(false);
    }
  };

  if (!cfg) return <div className={`${embedded ? "min-h-[60vh]" : "min-h-screen"} grid place-items-center bg-[#FBFAF7] font-display text-2xl text-[#4A0000]`}>Loading…</div>;
  if (phase === "confirmed") return (
    <div className={embedded ? "bg-[#FBFAF7]" : "min-h-screen bg-[#FBFAF7] grain"}>
      {!embedded && <LinkedInInsight />}
      <div className="max-w-lg mx-auto px-4 py-12">
        <Confirmation o={o} receipt={receipt} />
        {embedded && onClose && (
          <Button onClick={onClose} data-testid="booking-back-portal" className="mt-6 w-full bg-[#4A0000] hover:bg-[#5C1A1A] text-white">Back to portal</Button>
        )}
      </div>
    </div>
  );

  const r = receipt || {};
  const emailBody = encodeURIComponent(`Hi Reelty team, I'd like to book a shoot${o.address ? ` at ${o.address}` : ""}. My name is ${o.contact_name || "____"} and my phone is ${o.contact_phone || "____"}.`);

  return (
    <div className={`${embedded ? "" : "min-h-screen"} bg-[#FBFAF7] grain overflow-x-clip`} data-testid="booking-widget">
      {!embedded && <LinkedInInsight />}
      {/* Header */}
      <header className="border-b border-[#E9DDC6] bg-[#FBFAF7]/90 backdrop-blur-md sticky top-0 z-30">
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          {embedded
            ? <span className="shrink-0 w-8" aria-hidden="true" />
            : <a href="/" className="shrink-0"><BrandMark logoSize={30} textSize="text-2xl" /></a>}
          <h1 className="absolute left-1/2 -translate-x-1/2 font-display text-xl sm:text-2xl text-[#1A1715] leading-none whitespace-nowrap pointer-events-none">Book a shoot</h1>
          {embedded && onClose ? (
            <button onClick={onClose} data-testid="booking-close"
              className="flex items-center gap-1.5 text-sm font-medium text-[#4A0000] rounded-md px-3 py-1.5 border border-[#E9DDC6] hover:bg-[#4A0000] hover:text-white transition-colors">
              <X size={15} weight="bold" />Close
            </button>
          ) : (
            <span className="hidden sm:flex items-center gap-2 text-xs text-[#5C5A58]">
              <Lock size={13} weight="fill" className="text-[#B08D57]" /> Secure booking
            </span>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 overflow-x-clip">
        {/* Wizard */}
        <div className="lg:col-span-8 min-w-0">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: "easeOut" }}
            className="relative rounded-2xl border border-[#EADFC6] bg-gradient-to-b from-white to-[#FCFAF3] p-4 sm:p-8 shadow-[0_28px_70px_-34px_rgba(74,0,0,0.32)]">
            <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-[#C9A25A] via-[#E9C77E] to-[#B08D57]" />
            <QuestProgress steps={STEPS} step={step} testid="booking-quest" className="mb-6 sm:mb-7" />

            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.28 }}>
                {step === 0 && (
                  <StepAddress {...{ query, onQuery, sugg, selectAddress, busy, parcel, o, pickType, set, quote, inService }} />
                )}
                {step === 1 && (
                  <StepPackage {...{ cfg, o, r, pickPackage, toggleWaive, toggleItem, toggleAddon }} />
                )}
                {step === 2 && (
                  <StepPropertyType {...{ cfg, o, r, pickType, toggleOversized, sqft, acres, editSqft, editAcres }} />
                )}
                {step === 3 && (
                  <StepSchedule {...{ o, set, conflictTick }} />
                )}
                {step === 4 && (
                  <StepDetails {...{ o, set, err }} />
                )}
              </motion.div>
            </AnimatePresence>

            {/* Nav */}
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 mt-8 pt-5 border-t border-[#F0ECE2]">
              {step > 0 && (
                <Button variant="outline" data-testid="wizard-back" onClick={() => { setErr(""); setStep((s) => s - 1); }} className="w-full sm:w-auto justify-center border-[#E9DDC6] text-[#4A0000]">
                  <ArrowLeft size={16} className="mr-1.5" />Back
                </Button>
              )}
              {step < 4 ? (
                <Button data-testid="wizard-continue" disabled={!stepValid[step]} onClick={() => setStep((s) => s + 1)}
                  className="w-full sm:w-auto sm:ml-auto justify-center bg-[#4A0000] hover:bg-[#5C1A1A] text-white disabled:opacity-50">
                  Continue<ArrowRight size={16} className="ml-1.5" weight="bold" />
                </Button>
              ) : (
                <Button data-testid="pay-btn" disabled={busy || !stepValid[4]} onClick={checkout}
                  className="w-full sm:w-auto sm:ml-auto justify-center bg-[#4A0000] hover:bg-[#5C1A1A] text-white disabled:opacity-50">
                  {busy ? "Starting checkout…" : inService ? "Continue to payment" : "Request this shoot"}
                  <ArrowRight size={16} className="ml-1.5" weight="bold" />
                </Button>
              )}
            </div>
            {err && <p className="mt-3 text-sm text-[#B4472E]" data-testid="checkout-err">{err}</p>}
          </motion.div>

          {!embedded && (
            <p className="mt-4 text-xs text-[#5C5A58] text-center" data-testid="returning-login">
              Already work with Reelty? <a href="/login" target="_top" className="text-[#7E1119] font-semibold underline underline-offset-2">Log in to book faster →</a>
            </p>
          )}
        </div>

        {/* Summary rail */}
        <aside className="lg:col-span-4 min-w-0">
          <div className="lg:sticky lg:top-24 space-y-4">
            <OrderSummary {...{ o, r, parcel, acres, cfg, step }} />

            {/* 100% money-back guarantee — trust & confidence at the point of purchase */}
            <div className="relative flex items-center gap-3 rounded-xl border border-[#B08D57]/45 bg-gradient-to-r from-[#FBF6EA] to-[#F6EEDB] p-4 shadow-[0_8px_28px_-18px_rgba(74,0,0,0.4)]" data-testid="money-back-guarantee">
              <div className="relative shrink-0 grid place-items-center h-11 w-11 rounded-full bg-[#4A0000] text-[#E9C77E]">
                <SealCheck size={24} weight="fill" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#4A0000] leading-tight flex items-center gap-1.5">
                  100% Money-Back Guarantee
                </p>
                <p className="text-[11px] text-[#7a5c1a] leading-snug mt-0.5">Love your gallery or your money back. That's our promise on every shoot.</p>
              </div>
            </div>

            {/* Quick-book */}
            <div className="bg-white border border-[#E9DDC6] rounded-xl p-4" data-testid="quick-book">
              <p className="text-sm font-medium text-[#1A1715] flex items-center gap-1.5"><Headset size={15} weight="fill" className="text-[#B08D57]" />Prefer to talk to us?</p>
              <p className="text-xs text-[#5C5A58] mt-0.5">A producer will build the order with you.</p>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <a href={`tel:${PHONE}`} data-testid="quick-call" className="flex flex-col items-center gap-1 rounded-lg border border-[#4A0000] text-[#4A0000] py-2 text-xs font-medium hover:bg-[#4A0000] hover:text-white transition-colors"><Phone size={17} weight="fill" />Call</a>
                <a href={`sms:${PHONE}?&body=${emailBody}`} data-testid="quick-text" className="flex flex-col items-center gap-1 rounded-lg border border-[#4A0000] text-[#4A0000] py-2 text-xs font-medium hover:bg-[#4A0000] hover:text-white transition-colors"><ChatText size={17} weight="fill" />Text</a>
                <a href={`mailto:${EMAIL}?subject=${encodeURIComponent("Booking inquiry")}&body=${emailBody}`} data-testid="quick-email" className="flex flex-col items-center gap-1 rounded-lg border border-[#4A0000] text-[#4A0000] py-2 text-xs font-medium hover:bg-[#4A0000] hover:text-white transition-colors"><EnvelopeSimple size={17} weight="fill" />Email</a>
              </div>
              <p className="text-[11px] text-[#8A8782] mt-2 text-center">{PHONE} · {EMAIL}</p>
            </div>
          </div>
        </aside>
      </div>
      {!embedded && (
        <footer className="mt-10 pb-8 text-center" data-testid="booking-footer">
          <a href="/privacy" target="_top" className="text-xs text-[#8A8782] hover:text-[#7E1119] underline underline-offset-2 transition-colors" data-testid="booking-privacy-link">Privacy &amp; Cookies</a>
        </footer>
      )}
    </div>
  );
}
